import type { TargetingSearchResult } from '../../types/metaAdResources';
import { searchAllGeo } from './api';
import { COMMON_COUNTRIES, countryName } from './countries';

// Audience-builder — UNIFIED location search. ONE Zernio `geoType=all` call returns
// every location type in a single ranked list (each result carrying its own `type` +
// `path` breadcrumb); we route each result into its group by its OWN type and rank the
// account's likely country to the top using the breadcrumb. Behind this SINGLE
// `searchAllGeoTypes` seam so the UI never changed when the fan-out was swapped out.

export type GeoSearchType = 'country' | 'region' | 'city' | 'place' | 'neighborhood' | 'zip';
export type UnifiedGeoResult = TargetingSearchResult & { geoType: GeoSearchType };

// Static country "search" (there's no Zernio countries endpoint) over the committed list.
function searchCountriesStatic(q: string): UnifiedGeoResult[] {
  const ql = q.toLowerCase();
  return COMMON_COUNTRIES.filter(
    (c) => c.name.toLowerCase().includes(ql) || c.code.toLowerCase() === ql,
  ).map((c) => ({ id: c.code, name: c.name, type: 'country', path: null, audienceSize: null, geoType: 'country' as const }));
}

const GEO_TYPE_SET = new Set<string>(['country', 'region', 'city', 'place', 'neighborhood', 'zip']);

// A result's authoritative geoType is its OWN `type` field — NOT the geoType call that
// returned it. The same query can come back from multiple calls (a "Carina" neighborhood
// shows up in BOTH the city and neighborhood searches), and the city search returns items
// whose own type is "neighborhood". This drives the search dropdown's GROUPING (the result
// shows under its true Meta type — Neighborhoods, Cities, etc.). NOTE: it does NOT decide
// the targeting CHANNEL — on ADD, catchment locals (city + neighborhood/subcity) default
// to the radius-bearing cities[] channel regardless of this grouping (see
// geoHierarchy.applyGeoSelection); the channel controls the radius, not the Meta type.
// Unknown/missing types fall back to the queried type. Pure.
export function resolveGeoType(rawType: string | null | undefined, queried: GeoSearchType): GeoSearchType {
  const t = (rawType ?? '').trim().toLowerCase();
  if (t === 'state') return 'region';
  if (t === 'subcity') return 'neighborhood';
  return GEO_TYPE_SET.has(t) ? (t as GeoSearchType) : queried;
}

// Dedupe by result id (first-wins). The same geo key can return from several geoType
// calls; they describe the same entity (a key encodes its type+location), so the first
// copy is kept. Pure.
function dedupeById(results: UnifiedGeoResult[]): UnifiedGeoResult[] {
  const seen = new Set<string>();
  const out: UnifiedGeoResult[] = [];
  for (const r of results) {
    if (!r.id || seen.has(r.id)) continue;
    seen.add(r.id);
    out.push(r);
  }
  return out;
}

// The breadcrumb's country is its LAST element (path = [region, …, country] — ancestry,
// NOT the place's own name). Used to rank the account's likely country first.
function resultCountry(r: UnifiedGeoResult): string {
  return Array.isArray(r.path) && r.path.length > 0 ? (r.path[r.path.length - 1] ?? '').trim().toLowerCase() : '';
}

// Stable partition: results whose breadcrumb country matches the account's bias country
// lead, the rest keep Zernio's ranking after them. This replaces the old SCOPED Zernio
// call — `path` now carries the country in-process, so a second call is no longer needed.
// The list is GLOBAL either way (every country's results are present), so NZ-on-AU clients
// still see NZ results; bias only reorders, never filters.
function rankInCountryFirst(results: UnifiedGeoResult[], biasCountry: string | null): UnifiedGeoResult[] {
  if (!biasCountry) return results;
  const want = countryName(biasCountry).trim().toLowerCase();
  if (!want) return results;
  const inCountry: UnifiedGeoResult[] = [];
  const rest: UnifiedGeoResult[] = [];
  for (const r of results) (resultCountry(r) === want ? inCountry : rest).push(r);
  return [...inCountry, ...rest];
}

// ONE ranked `geoType=all` call (GLOBAL — no countryCode, so every country's results
// appear; the old hard AU scope hid NZ-client cities). Each result's geoType is its OWN
// `type` (resolveGeoType — a 'subcity' lands in Neighborhoods, etc.); the account's likely
// country is ranked first via the breadcrumb. A failed search yields no geo results (the
// static country matches still show). Deduped by id.
export async function searchAllGeoTypes(
  accountId: string,
  q: string,
  biasCountry: string | null,
): Promise<UnifiedGeoResult[]> {
  const query = q.trim();
  if (!query) return [];
  const countryResults = searchCountriesStatic(query); // static, global
  if (!accountId.trim()) return dedupeById(countryResults);

  let raw: TargetingSearchResult[] = [];
  try {
    raw = (await searchAllGeo(accountId, query, null)).results; // GLOBAL, all types, ranked
  } catch {
    raw = []; // a failed geo search still leaves the country matches
  }
  // Each result routes by its OWN type; fall back to 'city' only if Zernio omits a type.
  const mapped = raw.map((r) => ({ ...r, geoType: resolveGeoType(r.type, 'city') }));
  return dedupeById([...countryResults, ...rankInCountryFirst(mapped, biasCountry)]);
}

// Display order + section labels. Grouping by type avoids needing a cross-type relevance
// ranking we don't get from fan-out; within each group the type's own ranking is kept.
export const GEO_GROUP_ORDER: { geoType: GeoSearchType; label: string }[] = [
  { geoType: 'country', label: 'Countries' },
  { geoType: 'region', label: 'States / Regions' },
  { geoType: 'city', label: 'Cities' },
  { geoType: 'place', label: 'Suburbs / Places' },
  { geoType: 'neighborhood', label: 'Neighborhoods' },
  { geoType: 'zip', label: 'Postcodes' },
];

// Per-item type tag (shown alongside the name + breadcrumb).
export const GEO_TYPE_LABEL: Record<GeoSearchType, string> = {
  country: 'Country',
  region: 'State',
  city: 'City',
  place: 'Place',
  neighborhood: 'Neighborhood',
  zip: 'Postcode',
};

export type GeoResultGroup = { geoType: GeoSearchType; label: string; items: UnifiedGeoResult[] };

// Group the merged results by type, in display order, dropping empty groups. Pure.
export function groupGeoResults(results: UnifiedGeoResult[]): GeoResultGroup[] {
  return GEO_GROUP_ORDER.map((g) => ({
    ...g,
    items: results.filter((r) => r.geoType === g.geoType),
  })).filter((g) => g.items.length > 0);
}
